import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Plus, Trash2, Download, Mail } from 'lucide-react';
import { Invoice, InvoiceItem, CURRENCIES, InvoiceTemplate, INVOICE_TEMPLATES } from '../../types';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import TemplateSelector from './TemplateSelector';
import StripePayment from './StripePayment';
import toast from 'react-hot-toast';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../hooks/useAuth';

const schema = yup.object({
  invoiceNumber: yup.string().required('Invoice number is required'),
  date: yup.string().required('Date is required'),
  dueDate: yup.string().required('Due date is required'),
  company: yup.object({
    name: yup.string().required('Company name is required'),
    email: yup.string().email('Valid email is required').required('Email is required'),
    address: yup.string().required('Address is required'),
    phone: yup.string(),
    website: yup.string(),
  }),
  billTo: yup.object({
    name: yup.string().required('Client name is required'),
    email: yup.string().email('Valid email is required').required('Client email is required'),
    address: yup.string().required('Client address is required'),
  }),
  items: yup.array().of(
    yup.object({
      description: yup.string().required('Description is required'),
      quantity: yup.number().positive('Quantity must be positive').required('Quantity is required'),
      rate: yup.number().positive('Rate must be positive').required('Rate is required'),
    })
  ),
  taxRate: yup.number().min(0, 'Tax rate cannot be negative').max(100, 'Tax rate cannot exceed 100'),
  currency: yup.string().required('Currency is required'),
  notes: yup.string(),
  signature: yup.string().optional(),
  photos: yup.array().of(yup.string()).optional(),
});

interface InvoiceFormProps {
  onSave?: (invoice: Invoice) => void;
  initialData?: Partial<Invoice>;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ onSave, initialData }) => {
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate>(INVOICE_TEMPLATES[0]);
  const [showPayment, setShowPayment] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<InvoiceTemplate | null>(null);
  const [purchasedTemplates, setPurchasedTemplates] = useState<string[]>([]);
  const { user } = useAuth();
  const { hasAccessToTemplate, loading: loadingAccess } = useSubscription();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      invoiceNumber: initialData?.invoiceNumber || `INV-${Date.now()}`,
      date: initialData?.date || new Date().toISOString().split('T')[0],
      dueDate: initialData?.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      company: {
        name: initialData?.company?.name || '',
        businessName: initialData?.company?.businessName || '',
        email: initialData?.company?.email || '',
        address: initialData?.company?.address || '',
        phone: initialData?.company?.phone || '',
        mobile: initialData?.company?.mobile || '',
        fax: initialData?.company?.fax || '',
        website: initialData?.company?.website || '',
        businessNumber: initialData?.company?.businessNumber || '',
        additionalDetails: initialData?.company?.additionalDetails || false,
      },
      billTo: {
        name: initialData?.billTo?.name || '',
        email: initialData?.billTo?.email || '',
        address: initialData?.billTo?.address || '',
        phone: initialData?.billTo?.phone || '',
        mobile: initialData?.billTo?.mobile || '',
        fax: initialData?.billTo?.fax || '',
      },
      items: initialData?.items || [
        { id: '1', description: '', quantity: 1, rate: 0, amount: 0 }
      ],
      taxRate: initialData?.taxRate || 0,
      currency: initialData?.currency || 'USD',
      notes: initialData?.notes || '',
      signature: initialData?.signature || '',
      photos: initialData?.photos || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = watch('items');
  const watchedTaxRate = watch('taxRate');
  const watchedCurrency = watch('currency');

  // Calculate totals
  useEffect(() => {
    const items = watchedItems || [];
    const newSubtotal = items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const rate = Number(item.rate) || 0;
      const amount = quantity * rate;
      
      // Update amount in form
      const index = items.indexOf(item);
      setValue(`items.${index}.amount`, amount);
      
      return sum + amount;
    }, 0);

    const taxRate = Number(watchedTaxRate) || 0;
    const newTax = (newSubtotal * taxRate) / 100;
    const newTotal = newSubtotal + newTax;

    setSubtotal(newSubtotal);
    setTax(newTax);
    setTotal(newTotal);
  }, [watchedItems, watchedTaxRate, setValue]);

  // Efeito para verificar templates com acesso
  useEffect(() => {
    if (user && !loadingAccess) {
      // Crie uma lista de templates que o usuário tem acesso
      const accessibleTemplateIds = INVOICE_TEMPLATES
        .filter(template => !template.isPremium || hasAccessToTemplate(template.id))
        .map(template => template.id);

      setPurchasedTemplates(accessibleTemplateIds);
    }
  }, [user, loadingAccess, hasAccessToTemplate]);

  const addItem = () => {
    append({
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
    });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const getCurrencySymbol = (code: string) => {
    return CURRENCIES.find(c => c.code === code)?.symbol || '$';
  };

  const downloadPDF = (data: any) => {
    const invoice: Invoice = {
      id: initialData?.id || Date.now().toString(),
      ...data,
      subtotal,
      tax,
      total,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const pdf = generateInvoicePDF(invoice, selectedTemplate);
    pdf.save(`invoice-${data.invoiceNumber}.pdf`);
    toast.success('Invoice PDF downloaded successfully!');
  };

  const sendEmail = (data: any) => {
    // In a real app, this would integrate with an email service
    toast.success('Email feature coming soon!');
  };

  const handleTemplateSelect = (template: InvoiceTemplate) => {
    setSelectedTemplate(template);
  };

  const handlePurchaseTemplate = (template: InvoiceTemplate) => {
    if (!user) {
      toast.error("Por favor, faça login para comprar templates premium");
      return;
    }

    // Verificar se o usuário já tem acesso a este template
    if (hasAccessToTemplate(template.id)) {
      toast.success(`Você já tem acesso ao template "${template.name}"!`);
      setSelectedTemplate(template);
      return;
    }

    setPendingTemplate(template);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (template: InvoiceTemplate) => {
    setPurchasedTemplates(prev => [...prev, template.id]);
    setSelectedTemplate(template);
    setPendingTemplate(null);
    toast.success(`Template "${template.name}" desbloqueado com sucesso!`);
  };

  const onSubmit = (data: any) => {
    const invoice: Invoice = {
      id: initialData?.id || Date.now().toString(),
      ...data,
      subtotal,
      tax,
      total,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    if (onSave) {
      onSave(invoice);
      toast.success('Invoice saved successfully!');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Template Selector */}
      <TemplateSelector
        selectedTemplate={selectedTemplate}
        onTemplateSelect={handleTemplateSelect}
        onPurchaseTemplate={handlePurchaseTemplate}
        userAccessibleTemplates={purchasedTemplates}
        loadingAccess={loadingAccess}
      />

      {/* Stripe Payment Modal */}
      {pendingTemplate && (
        <StripePayment
          template={pendingTemplate}
          isOpen={showPayment}
          onClose={() => {
            setShowPayment(false);
            setPendingTemplate(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Invoice Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number
              </label>
              <input
                {...register('invoiceNumber')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.invoiceNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.invoiceNumber.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                {...register('date')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                {...register('dueDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">From (Your Company)</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                {...register('company.name')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.company?.name && (
                <p className="mt-1 text-sm text-red-600">{errors.company.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name
              </label>
              <input
                {...register('company.businessName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                {...register('company.email')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.company?.email && (
                <p className="mt-1 text-sm text-red-600">{errors.company.email.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <textarea
                {...register('company.address')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.company?.address && (
                <p className="mt-1 text-sm text-red-600">{errors.company.address.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                {...register('company.phone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Number
              </label>
              <input
                {...register('company.businessNumber')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <input
                {...register('company.website')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile
              </label>
              <input
                {...register('company.mobile')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fax
              </label>
              <input
                {...register('company.fax')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mt-2 flex items-center">
              <input
                type="checkbox"
                id="showAdditionalDetails"
                {...register('company.additionalDetails')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showAdditionalDetails" className="ml-2 block text-sm font-medium text-gray-700">
                Show additional business details
              </label>
            </div>
          </div>
        </div>

        {/* Bill To Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill To (Client)</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name *
              </label>
              <input
                {...register('billTo.name')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.billTo?.name && (
                <p className="mt-1 text-sm text-red-600">{errors.billTo.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Email *
              </label>
              <input
                type="email"
                {...register('billTo.email')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.billTo?.email && (
                <p className="mt-1 text-sm text-red-600">{errors.billTo.email.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Address *
              </label>
              <textarea
                {...register('billTo.address')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.billTo?.address && (
                <p className="mt-1 text-sm text-red-600">{errors.billTo.address.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                {...register('billTo.phone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile
              </label>
              <input
                {...register('billTo.mobile')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fax
              </label>
              <input
                {...register('billTo.fax')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Items</h3>
            <button
              type="button"
              onClick={addItem}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Item</span>
            </button>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    {...register(`items.${index}.description`)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Item description"
                  />
                  {errors.items?.[index]?.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.items[index]?.description?.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    {...register(`items.${index}.quantity`)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                  {errors.items?.[index]?.quantity && (
                    <p className="mt-1 text-sm text-red-600">{errors.items[index]?.quantity?.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate ({getCurrencySymbol(watchedCurrency)})
                  </label>
                  <input
                    type="number"
                    {...register(`items.${index}.rate`)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                  {errors.items?.[index]?.rate && (
                    <p className="mt-1 text-sm text-red-600">{errors.items[index]?.rate?.message}</p>
                  )}
                </div>

                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount
                    </label>
                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                      {getCurrencySymbol(watchedCurrency)} {((watchedItems[index]?.quantity || 0) * (watchedItems[index]?.rate || 0)).toFixed(2)}
                    </div>
                  </div>
                  
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800 transition-colors duration-200 p-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals and Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    {...register('currency')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {CURRENCIES.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name} ({currency.symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    {...register('taxRate')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  {errors.taxRate && (
                    <p className="mt-1 text-sm text-red-600">{errors.taxRate.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Total</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{getCurrencySymbol(watchedCurrency)} {subtotal.toFixed(2)}</span>
                </div>
                
                {tax > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600">Tax ({watchedTaxRate}%):</span>
                    <span className="font-medium">{getCurrencySymbol(watchedCurrency)} {tax.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center py-3 border-t-2 border-blue-600">
                  <span className="text-lg font-semibold text-gray-900">Total:</span>
                  <span className="text-lg font-bold text-blue-600">{getCurrencySymbol(watchedCurrency)} {total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
          <textarea
            {...register('notes')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Additional notes, payment terms, etc."
          />
        </div>

        {/* Signature */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature</h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <input
              type="file"
              id="signatureFile"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    if (event.target?.result) {
                      setValue('signature', event.target.result as string);
                    }
                  };
                  reader.readAsDataURL(e.target.files[0]);
                }
              }}
            />
            <label
              htmlFor="signatureFile"
              className="cursor-pointer inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              Upload Signature
            </label>
            {watch('signature') && (
              <div className="mt-4">
                <img 
                  src={watch('signature')} 
                  alt="Signature" 
                  className="max-h-24 mx-auto"
                />
                <button
                  type="button"
                  className="mt-2 text-red-600 text-sm hover:underline"
                  onClick={() => setValue('signature', undefined)}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Photos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Photos</h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              id="photoFile"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    if (event.target?.result) {
                      const currentPhotos = watch('photos') || [];
                      setValue('photos', [...currentPhotos, event.target.result as string]);
                    }
                  };
                  reader.readAsDataURL(e.target.files[0]);
                }
              }}
            />
            <div className="flex flex-wrap gap-4">
              {(watch('photos') || []).map((photo, index) => (
                <div key={index} className="relative">
                  <img src={photo} alt={`Photo ${index + 1}`} className="w-32 h-32 object-cover rounded-lg" />
                  <button
                    type="button"
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs"
                    onClick={() => {
                      const currentPhotos = watch('photos') || [];
                      setValue(
                        'photos',
                        currentPhotos.filter((_, i) => i !== index)
                      );
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <label
                htmlFor="photoFile"
                className="w-32 h-32 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
              >
                <div className="text-center">
                  <div className="text-blue-600 text-xl mb-1">+</div>
                  <div className="text-sm text-gray-600">Add Photo</div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={handleSubmit(downloadPDF)}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center justify-center space-x-2 font-medium"
          >
            <Download className="h-5 w-5" />
            <span>Download PDF</span>
          </button>

          <button
            type="button"
            onClick={handleSubmit(sendEmail)}
            className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center justify-center space-x-2 font-medium"
          >
            <Mail className="h-5 w-5" />
            <span>Send Email</span>
          </button>

          {onSave && (
            <button
              type="submit"
              className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center justify-center space-x-2 font-medium"
            >
              <span>Save Invoice</span>
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;